import { useTranslation } from "react-i18next"

import type { M365ApprovalStatus } from "@/types/user-settings"

import { Cloud, Loader2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  M365ConsentBody,
  M365ConsentHeading,
} from "@/features/ai-explorer/components/m365-consent-content"

/**
 * Microsoft 365 consent inside the wizard. Renders the same scope description
 * and approval notice as the standalone `M365InfoModal`; both buttons persist a
 * decision (see `chooseM365` in onboarding-page.tsx), so this is the one place
 * a new user is asked.
 */
export function OnboardingM365Step({
  busy,
  pending,
  onDecide,
}: {
  busy: boolean
  /** Which decision is being saved right now, so the spinner sits on that button. */
  pending: M365ApprovalStatus | null
  onDecide: (status: M365ApprovalStatus) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="mx-auto max-w-3xl" data-testid="onboarding-m365-step">
      <div className="flex items-start gap-4">
        <div className="bg-primary/10 text-primary flex size-12 shrink-0 items-center justify-center rounded-lg">
          <Cloud aria-hidden="true" className="size-6" />
        </div>
        <div>
          <h1
            data-onboarding-step-heading
            className="font-display flex flex-wrap items-center gap-2 text-2xl font-semibold sm:text-3xl"
            tabIndex={-1}
          >
            <M365ConsentHeading title={t("personalization.smoothOnboarding.m365.title")} />
          </h1>
          <p className="text-muted-foreground mt-2">
            {t("personalization.smoothOnboarding.m365.description")}
          </p>
        </div>
      </div>
      <div className="border-border bg-background mt-6 max-h-[48vh] overflow-y-auto rounded-lg border p-4 sm:p-5">
        <M365ConsentBody />
      </div>
      <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button
          className="min-h-10"
          data-testid="onboarding-m365-decline"
          disabled={busy}
          variant="outline"
          onClick={() => onDecide("declined")}
        >
          {pending === "declined" && <Loader2 aria-hidden="true" className="size-4 animate-spin" />}
          {t("m365_tool.info.decline")}
        </Button>
        <Button
          className="min-h-10"
          data-testid="onboarding-m365-approve"
          disabled={busy}
          onClick={() => onDecide("approved")}
        >
          {pending === "approved" && <Loader2 aria-hidden="true" className="size-4 animate-spin" />}
          {t("m365_tool.info.approve")}
        </Button>
      </div>
    </div>
  )
}
